import { useCallback } from 'react';
import { Form, getIn, useFormikContext } from 'formik';
import type { FormikErrors, FormikTouched } from 'formik';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';

import { invariant } from '@e-socna/utils';
import { EMPTY_OBJECT } from '@e-socna/empty';
import type { EMPTY_STRING } from '@e-socna/empty';
import type { CostMarginId, CostTypeId, CurrencyId } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { LazyEditor } from '../../components/Editor/Lazy';
import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { AMOUNT_VALUE_FIELD_STEP } from '../../constants/number-field-steps.constant';
import { IdSelect } from '../../components/IdSelect';
import type { IdOption } from '../../components/IdSelect';

import { MemoBeforeSubmitForm } from '../MemoBeforeSubmit';
import { memoAwareSubmitHandler } from '../MemoBeforeSubmit/utils/memo-aware-submit-handler.util';
import { FormVariant } from '../enums/variant.enum';
import type { useMemos } from '../MemoBeforeSubmit/hooks/useMemos.hook';
import { AlertMessages } from '../../components/AlertMessages';

import type { CostForm_Cost } from './fragments/cost.fragment';
import type { CostForm_FixedCost } from './fragments/fixed-cost.fragment';
import type { CostForm_VariableCost } from './fragments/variable-cost.fragment';

interface Props {
  readonly askMemoBeforeSubmit: boolean;
  readonly clearMemos: ReturnType<typeof useMemos>['clearMemos'];
  readonly formError: readonly string[] | null;
  readonly formVariant?: FormVariant;
  readonly initialValues: CostForm_Cost;
  readonly memos: ReturnType<typeof useMemos>['memos'];
  readonly onCancel?: () => void;
  readonly setMemos: ReturnType<typeof useMemos>['setMemos'];
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly submitLabel: string;
  readonly title?: string;
}

export const CostFormFields = ({
  askMemoBeforeSubmit,
  clearMemos,
  formError,
  formVariant,
  initialValues,
  memos,
  onCancel: handleCancel,
  setMemos,
  setMustBlockAction,
  showCancelButton,
  submitLabel,
  title,
}: Readonly<Props>) => {
  const { ui } = useUiContext();

  const {
    costTypesCodeMap,
    costMargins,
    costFileCategoriesCodeMap,
    currencies,
    costTypes,
    fileDirectoryCategoriesCodeMap,
  } = useReferencesContext();

  const {
    dirty,
    errors,
    handleBlur,
    handleChange,
    handleSubmit: formikSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    submitForm,
    touched,
    values,
  } = useFormikContext<CostForm_Cost>();

  invariant('code' in values.codingConfig, 'Unexpected codingConfig type');

  const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

  const handleTypeIdChange = useCallback(
    (id: CostTypeId | typeof EMPTY_STRING) => {
      void setFieldValue('typeId', id);
    },
    [setFieldValue],
  );

  const handleCurrencyIdChange = useCallback(
    (id: CurrencyId | typeof EMPTY_STRING) => {
      void setFieldValue('amount.currencyId', id);
    },
    [setFieldValue],
  );

  const handleCostMarginIdChange = useCallback(
    (id: CostMarginId | typeof EMPTY_STRING) => {
      void setFieldValue('costMarginId', id);
    },
    [setFieldValue],
  );

  const handleMemoDialogOpen = useCallback(() => {
    setMemos((oldMemos) => ({
      ...oldMemos,
      isOpenMemoDialog: true,
    }));
  }, [setMemos]);

  return (
    <Form
      onSubmit={memoAwareSubmitHandler({
        askMemo: handleMemoDialogOpen,
        mustAskMemo: askMemoBeforeSubmit && dirty && isValid,
        submit: formikSubmit,
      })}
      // eslint-disable-next-line react/forbid-component-props
      style={FORM_LAYOUT_STYLE}
    >
      <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!askMemoBeforeSubmit && (
        <MemoBeforeSubmitForm
          clearMemos={clearMemos}
          isLoading={isLoading}
          memos={memos}
          onConfirm={submitForm}
          setMemos={setMemos}
          submitLabel={submitLabel}
        />
      )}
      <FormLayout
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              disabled={!isValid}
              loading={isLoading}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitLabel}
            </Button>
            {!!showCancelButton && (
              <Button onClick={handleCancel} size="large" variant="outlined">
                Annuler
              </Button>
            )}
          </Stack>
        }
        error={
          !!formError && <AlertMessages messages={formError} severity="error" />
        }
        title={title}
      >
        <Grid container spacing={2}>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <IdSelect<CostTypeId, IdOption<CostTypeId>>
              disabled={formVariant === FormVariant.Modification}
              error={!!(errors.typeId && touched.typeId)}
              helperText={
                !!(errors.typeId && touched.typeId) &&
                (getIn(errors, 'typeId') as string)
              }
              id="costTypeId"
              label="Type de Coût"
              name="typeId"
              onBlur={handleBlur}
              onChange={handleTypeIdChange}
              options={costTypes.map((costType) => ({
                id: costType._id,
                label: costType.label,
              }))}
              required
              value={values.typeId as CostTypeId}
            />
          </Grid>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
            sx={{ pt: 1, pb: 1 }}
          >
            <TextField
              error={
                !!(
                  getIn(errors, 'codingConfig.code') &&
                  getIn(touched, 'codingConfig.code')
                )
              }
              fullWidth
              helperText={getTouchedError(errors, touched, 'codingConfig.code')}
              id="cost-coding-config-code"
              label="Code"
              name="codingConfig.code"
              onBlur={handleBlur}
              onChange={handleChange}
              required
              type="text"
              value={values.codingConfig.code}
              variant="outlined"
            />
          </Grid>
          <Grid
            size={{
              md: 8,
              xs: 12,
            }}
            sx={{ pt: 1, pb: 1 }}
          >
            <TextField
              error={!!(errors.label && touched.label)}
              fullWidth
              helperText={getTouchedError(errors, touched, 'label')}
              id="cost-label"
              label="Libellé"
              name="label"
              onBlur={handleBlur}
              onChange={handleChange}
              required
              type="text"
              value={values.label}
              variant="outlined"
            />
          </Grid>
          {values.typeId === costTypesCodeMap.FixedCost._id && (
            <>
              <Grid
                size={{
                  sm: 6,
                  xs: 12,
                }}
                sx={{ pt: 1, pb: 1 }}
              >
                <TextField
                  error={
                    !!(
                      (errors as FormikErrors<CostForm_FixedCost>).amount
                        ?.value &&
                      (touched as FormikTouched<CostForm_FixedCost>).amount
                        ?.value
                    )
                  }
                  fullWidth
                  helperText={
                    (errors as FormikErrors<CostForm_FixedCost>).amount?.value
                  }
                  id="fixed-cost-amount"
                  label="Valeur"
                  name="amount.value"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  required
                  slotProps={{
                    htmlInput: {
                      min: AMOUNT_VALUE_FIELD_STEP,
                      step: AMOUNT_VALUE_FIELD_STEP,
                    },
                  }}
                  type="number"
                  value={(values as CostForm_FixedCost).amount.value}
                  variant="outlined"
                />
              </Grid>
              <Grid
                size={{
                  sm: 6,
                  xs: 12,
                }}
                sx={{ pt: 1, pb: 1 }}
              >
                <IdSelect<CurrencyId, IdOption<CurrencyId>>
                  error={
                    !!(
                      (errors as FormikErrors<CostForm_FixedCost>).amount
                        ?.currencyId &&
                      (touched as FormikTouched<CostForm_FixedCost>).amount
                        ?.currencyId
                    )
                  }
                  helperText={
                    !!(
                      (errors as FormikErrors<CostForm_FixedCost>).amount
                        ?.currencyId &&
                      (touched as FormikTouched<CostForm_FixedCost>).amount
                        ?.currencyId
                    ) && (getIn(errors, 'amount.currencyId') as string)
                  }
                  id="fixedCost-currencyId"
                  label="Devise"
                  name="amount.currencyId"
                  onBlur={handleBlur}
                  onChange={handleCurrencyIdChange}
                  options={currencies.map((currency) => ({
                    id: currency._id,
                    label: currency.label,
                  }))}
                  required
                  value={
                    (values as CostForm_FixedCost).amount
                      .currencyId as CurrencyId
                  }
                />
              </Grid>
            </>
          )}
          {values.typeId === costTypesCodeMap.VariableCost._id && (
            <Grid size={12} sx={{ pt: 1, pb: 1 }}>
              <TextField
                error={
                  !!(
                    (errors as FormikErrors<CostForm_VariableCost>).value &&
                    (touched as FormikTouched<CostForm_VariableCost>).value
                  )
                }
                fullWidth
                helperText={getTouchedError(errors, touched, 'value')}
                id="variable-cost-value"
                label="Valeur"
                name="value"
                onBlur={handleBlur}
                onChange={handleChange}
                required
                slotProps={{
                  htmlInput: {
                    min: AMOUNT_VALUE_FIELD_STEP,
                    max: 100,
                    step: AMOUNT_VALUE_FIELD_STEP,
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                }}
                type="number"
                value={(values as CostForm_VariableCost).value}
                variant="outlined"
              />
            </Grid>
          )}
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <IdSelect<CostMarginId, IdOption<CostMarginId>>
              error={!!(errors.costMarginId && touched.costMarginId)}
              helperText={
                !!(errors.costMarginId && touched.costMarginId) &&
                (getIn(errors, 'costMarginId') as string)
              }
              id="costMarginId"
              label="Marge sur Coût"
              name="costMarginId"
              onBlur={handleBlur}
              onChange={handleCostMarginIdChange}
              options={costMargins.map((costMargin) => ({
                id: costMargin._id,
                label: costMargin.label,
              }))}
              required
              value={values.costMarginId as CostMarginId}
            />
          </Grid>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <TextField
              error={!!(errors.title && touched.title)}
              fullWidth
              helperText={getTouchedError(errors, touched, 'title')}
              id="cost-title"
              label="Titre"
              name="title"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.title}
              variant="outlined"
            />
          </Grid>
          <Grid
            size={12}
            sx={{ display: 'flex', flexDirection: 'column', pt: 1, pb: 1 }}
          >
            <LazyEditor
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(errors.content
                ? {
                    alert: {
                      severity: 'error',
                      content: errors.content,
                    },
                  }
                : EMPTY_OBJECT)}
              disabled={isSubmitting}
              error={!!(errors.content && touched.content)}
              id={values._id}
              initialValue={initialValues.content}
              label="Contenu"
              name="content"
              onBlur={handleBlur}
              onChange={handleChange}
              required={false}
              resolversSettings={{
                allowedReturnTypeIds: undefined,
                enableDirectGeneration: false,
                mustBeResolvableOnBack: false,
              }}
              storage={{
                directoryCategoryId: fileDirectoryCategoriesCodeMap.Cost._id,
                entityId: values._id,
                imageCategory: costFileCategoriesCodeMap.TextEditorImages,
              }}
              value={values.content}
            />
          </Grid>
        </Grid>
      </FormLayout>
    </Form>
  );
};
